import React from 'react';
import SwCheckbox from '../Common/FormComponent/SwCheckbox';
import SwBadge from '../Common/SwBadge/SwBadge';
import {
  OUTCOME_META,
  statusMetaFor,
  resultFor,
  offersForUser,
  offersAcross,
  offerPickCount,
  isOfferActionable,
  actionableOffers,
  fmtNumber,
} from './mealPlanUsersData';

/**
 * What a confirmation is about to do, employee by employee and plan by plan.
 *
 * A meal type is not one plan. "Lunch" can be served by two wallet offers, and
 * an employee can hold both — so removing them from Lunch is ambiguous until
 * someone says which offer. This table asks: every plan the employee holds is
 * listed by its wallet offer name with a checkbox, ticked to begin with, and
 * unticking one leaves that plan alone.
 *
 * A single-plan meal type gets exactly the same shape, one checkbox instead of
 * two. It costs one click that could have been skipped and buys the SPOC the
 * plan's actual name — which the old layout never showed at all, so a dialog
 * headed "the Breakfast plan" never said it meant VegMeal.
 *
 * Each line's consequence is the endpoint's own dry run for that exact
 * employee-and-plan pair, so the table can never promise something the write
 * would refuse.
 *
 * A plan already in the state being asked for is listed but not tickable: on an
 * assignment the ones the employee is already Active on, on a removal the ones
 * they are already Restricted from. The endpoint answers "no_change" for both,
 * so a tick there is a click that does nothing — and it would count towards the
 * confirm button. They stay visible, greyed and labelled, because knowing that
 * Chirag is already on VegLunchDev is the reason the row is worth reading.
 */

const outcomeText = (result, action) => {
  if (!result) return '…';

  if (result.outcome === 'no_change') {
    return `No change — ${result.reason || 'nothing to do'}.`;
  }

  const label = OUTCOME_META[result.outcome]?.label || result.outcome;
  const kept = result.keptPoints;
  /* On a removal the points the employee keeps are the consequence that
     matters, so they travel with the label rather than in a column of their
     own that would be empty for an assignment. */
  return action === 'unassign' && kept ? `${label} · keeps ${fmtNumber(kept)} points` : label;
};

const MealPlanReviewTable = ({
  action, // 'assign' | 'unassign'
  offers = [], // the company's plans for this meal type, used as a fallback
  userIds = [],
  rowFor,
  selection = {},
  onSelectionChange,
  results = [],
  disabled = false,
}) => {
  const togglePlan = (userId, offerId) => {
    if (disabled) return;
    const current = selection[userId] || [];
    onSelectionChange({
      ...selection,
      [userId]: current.includes(offerId)
        ? current.filter(id => id !== offerId)
        : [...current, offerId],
    });
  };

  /* Only the employees this plan can actually be changed for. Counting the rest
     would make "2/3" mean "one of them was never eligible", which reads as a
     bug rather than as a state. */
  const actionableHolders = offerId =>
    userIds.filter(id =>
      actionableOffers(rowFor?.(id), offers, action).some(o => o.id === offerId),
    );

  /* One plan, everybody. If they all have it, the click takes it off them all;
     otherwise it goes on. That makes "unassign VegMeal from this whole list"
     a single click, which is the reason bulk selection exists at all. */
  const toggleForEveryone = offerId => {
    if (disabled) return;
    const holders = actionableHolders(offerId);
    if (!holders.length) return;
    const turningOn = offerPickCount(selection, holders, offerId) < holders.length;

    const next = { ...selection };
    holders.forEach(userId => {
      const current = next[userId] || [];
      if (turningOn && !current.includes(offerId)) next[userId] = [...current, offerId];
      if (!turningOn) next[userId] = current.filter(id => id !== offerId);
    });
    onSelectionChange(next);
  };

  /* A plan nobody can be changed on gets no bulk control at all. */
  const acrossOffers = offersAcross(userIds, rowFor, offers).filter(
    offer => actionableHolders(offer.id).length > 0,
  );
  const showBulkBar = userIds.length > 1 && acrossOffers.length > 0;

  return (
    <>
      {/* MPC-7: the bulk control sits above the list, not inside a column
          header, so it reads as an action on the whole list rather than as one
          more checkbox to get confused with the per-employee ones. */}
      {showBulkBar ? (
        <div className="MealPlanUsers_BulkBar mb-3">
          <span className="MealPlanUsers_BulkBarLabel">Apply to everyone</span>
          <div className="MealPlanUsers_BulkBarPlans">
            {acrossOffers.map(offer => {
              const holders = actionableHolders(offer.id);
              const picked = offerPickCount(selection, holders, offer.id);
              const all = picked === holders.length && picked > 0;

              return (
                <SwCheckbox
                  key={offer.id}
                  id={`review-all-${offer.id}`}
                  className="MealPlanUsers_BulkChip"
                  label={
                    <>
                      {offer.name}
                      {/* The tally earns its place when it is partial: the box
                          is unticked, yet some of these people already have the
                          plan. */}
                      <span className="MealPlanUsers_BulkChipCount">
                        {picked}/{holders.length}
                      </span>
                    </>
                  }
                  checked={all}
                  disabled={disabled}
                  onChange={() => toggleForEveryone(offer.id)}
                />
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="MealPlanUsers_ReviewScroll">
        <table className="table table-bordered MealPlanUsersReviewTable mb-3">
          <thead>
            <tr>
              <th>Employee</th>
              <th>{action === 'unassign' ? 'Plans to remove' : 'Plans to assign'}</th>
            </tr>
          </thead>
          <tbody>
            {userIds.map(userId => {
              const row = rowFor?.(userId);
              const userOffers = offersForUser(row, offers);
              const picked = selection[userId] || [];

              return (
                <tr
                  key={userId}
                  className={picked.length ? undefined : 'MealPlanUsers_ReviewRowOff'}
                >
                  <td className="MealPlanUsers_ReviewWho">
                    <span className="fw-600">{row?.name || `User ${userId}`}</span>
                    {row?.email ? (
                      <>
                        <br />
                        <span className="SwTextGray SwTextSmall">{row.email}</span>
                      </>
                    ) : null}
                  </td>

                  <td>
                    {!userOffers.length ? (
                      <span className="SwTextGray SwTextSmall">
                        No plans found for this employee in this meal type.
                      </span>
                    ) : (
                      <ul className="MealPlanUsers_PlanChoices">
                        {userOffers.map(offer => {
                          const actionable = isOfferActionable(offer, action);
                          const on = actionable && picked.includes(offer.id);
                          const meta = offer.status ? statusMetaFor(offer.status) : null;

                          return (
                            <li
                              key={offer.id}
                              className={
                                actionable
                                  ? 'MealPlanUsers_PlanChoice'
                                  : 'MealPlanUsers_PlanChoice MealPlanUsers_PlanChoiceLocked'
                              }
                            >
                              <SwCheckbox
                                id={`review-${userId}-${offer.id}`}
                                label={offer.name}
                                checked={on}
                                disabled={disabled || !actionable}
                                onChange={() => togglePlan(userId, offer.id)}
                              />

                              {/* The employee's state for this plan today, and
                                  below it what the write would do to it. */}
                              {meta ? (
                                <SwBadge
                                  variant={meta.variant}
                                  icon={<i className={`bi ${meta.icon}`} />}
                                  text={meta.label}
                                />
                              ) : null}

                              <span className="SwTextGray SwTextSmall MealPlanUsers_PlanChoiceNote">
                                {!actionable
                                  ? action === 'assign'
                                    ? 'Already on this plan — nothing to assign.'
                                    : 'Not on this plan — nothing to remove.'
                                  : on
                                  ? outcomeText(resultFor(results, userId, offer.id), action)
                                  : 'Not selected — left as it is.'}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}

                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default MealPlanReviewTable;
